// 클릭·제출 기록에 붙일 «이름»을 정한다 — «어느 구역에서 무엇을 했나»가 읽히게(2026-10-05).
// 드림(dreamcus src/lib/activityName.ts)의 «구역 · 행동» 규칙을 핸드폰 화면에 맞게 옮겼다.
//
// 이름은 `구역 · 행동` 두 토막이다. 예: «메뉴 · 이동 /crm/customers», «창 · 저장», «이름 없는 버튼».
//   구역 = 가장 가까운 조상의 data-activity-area → 없으면 화면의 큰 칸(창·메뉴·상단바)
//   행동 = 아래 순서로 처음 잡히는 것
//     ① 요소 자신의 data-activity-name — 개발자가 코드에 박은 이름
//     ② aria-label·title·버튼 글자가 **고정 문구 목록(FIXED_LABELS)과 정확히 같을 때만** 그 문구
//     ③ 링크면 목적지 경로(동적 조각·번호·이메일 꼴은 :id, 쿼리 버림)
//     ④ 같은 구역 안 조상의 data-activity-name — 줄 전체가 눌리는 목록·표는 이름을 한 번만 붙이면 된다
//     ⑤ 요소 종류로 풀어 쓴 이름(«이름 없는 버튼»·«표 클릭»·«검색창»…)
//
// 🔒 허용 목록 방식(2026-10-05 보안 검토 두 번째 반영) — 핸드폰 화면엔 고객 이름·전화번호·주소가 그대로 있고
//    행동 기록은 S3 로 영구 백업된다. 처음엔 «모양으로 위험한 글자만 뺀다»였는데, 이름은 모양으로 가려낼 수
//    없어 계속 샜다. 그래서 뒤집었다: **화면 글자는 코드에 박힌 고정 문구와 똑같을 때만 쓰고, 그 밖의 글자는
//    어디서 왔든(버튼·카드·표·입력칸·aria-label) 아예 안 쓴다.** 모르면 «이름 없는 버튼». 더 정확한 이름이
//    필요한 버튼은 data-activity-name 을 붙인다 — 그게 정석이다. 감춰야 할 칸은 data-activity-private.
//    마지막에 shared/activity-redact 로 번호·이메일을 한 번 더 가린다(data-* 이름표에 실수로 값이 들어가도).

import { redactActivityText } from "../../shared/activity-redact";

/** 이름 짓기에 필요한 만큼만 — 테스트에서 가짜 요소로도 부를 수 있게 DOM 전체를 요구하지 않는다. */
export type NamedNode = {
  tagName: string;
  textContent: string | null;
  parentElement: NamedNode | null;
  getAttribute(name: string): string | null;
};

// 고정 문구 — 화면 코드에 박혀 있는 버튼·탭 글자. **여기 있는 글자와 정확히 같을 때만** 이름이 된다.
// 고객 데이터(이름·모델·매장명)는 여기 넣지 않는다. 새 버튼은 여기보다 data-activity-name 을 붙이는 게 낫다.
const FIXED_LABELS = new Set([
  // 공통 동작
  "저장", "취소", "삭제", "확인", "닫기", "검색", "추가", "수정", "편집", "등록", "적용", "초기화",
  "새로고침", "다음", "이전", "완료", "복사", "전송", "보내기", "발송", "업로드", "다운로드", "내려받기",
  "내보내기", "가져오기", "선택", "전체", "전체 선택", "선택 해제", "더보기", "접기", "펼치기", "필터",
  "정렬", "보기", "열기", "미리보기", "출력", "인쇄", "로그인", "로그아웃", "설정", "메뉴", "홈", "뒤로",
  "확정", "승인", "반려", "해제", "변경", "생성", "만들기", "제출", "예", "아니오", "네", "아니요",
  "빼기", "넣기", "되돌리기", "다시 시도", "건너뛰기", "계속", "처음으로", "목록", "목록으로", "상세",
  "이번 달", "오늘", "어제", "이번 주", "지난 달", "다음 달", "전체 보기", "보러 가기", "처리 중",
  // 핸드폰 업무 문구(코드 상수)
  "엑셀 내보내기", "엑셀 다운로드", "엑셀 업로드", "링크 발급", "링크 복사", "링크 만료", "보류 풀기",
  "매장 전환", "레이아웃 수정", "자동완성", "서식", "텍스트", "텍스트 변환", "서식 출력", "신청서 보기",
  "고객 추가", "새 고객", "문자 보내기", "전화 걸기", "메모", "행 넣기", "표 칼럼",
  "시세표", "정책", "고객", "신청서", "재고", "상담", "매장", "직원", "회원", "구두", "액면",
  "개통", "개통 완료", "개통 대기", "정산", "업로드 내역", "매핑", "미매핑",
  "SKT", "KT", "LGU+", "LG U+",
]);

const TABLE = new Set(["table", "thead", "tbody", "tr", "td", "th"]);
const LIST = new Set(["ul", "ol", "li", "dl", "dt", "dd"]);
const HEADING = new Set(["h1", "h2", "h3", "h4", "h5", "h6"]);
const PICTURE = new Set(["img", "svg", "path", "canvas", "picture", "g", "use"]);
const INPUT_KIND: Record<string, string> = {
  search: "검색창",
  checkbox: "체크 상자",
  radio: "선택 단추",
  file: "파일 선택",
  date: "날짜 입력",
  number: "숫자 입력",
  submit: "제출 버튼",
  button: "버튼",
};

/** 경로의 동적 조각(:id·토큰 등)을 정규화해 한 페이지로 묶는다 — /crm/customers/123 → /crm/customers/:id. */
export function normalizeActivityPath(pathname: string): string {
  return pathname
    .split("/")
    .map((seg) => {
      if (/^\d+$/.test(seg)) return ":id";
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(seg)) return ":id";
      if (seg.length >= 20) return ":id"; // 공개 폼 토큰 등 긴 랜덤 조각
      if (/\d(?:\D?\d){5,}/.test(seg) || seg.includes("@") || seg.includes("%40")) return ":id"; // 번호·이메일 꼴
      return seg;
    })
    .join("/");
}

/** 화면 글자 → 고정 문구 목록에 있는 글자면 그 문구, 아니면 null. 아이콘(이모지·기호)과 겹친 띄어쓰기만 걷어 비교한다. */
export function fixedLabel(text: string | null | undefined): string | null {
  if (!text) return null;
  const t = text
    .replace(/[^\p{L}\p{N}+ ]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
  return FIXED_LABELS.has(t) ? t : null;
}

function explicitOf(el: NamedNode) {
  return el.getAttribute("data-activity-name");
}

function isPrivate(el: NamedNode) {
  for (let n: NamedNode | null = el; n; n = n.parentElement) {
    if (n.getAttribute("data-activity-private") !== null) return true;
  }
  return false;
}

function isInteractive(el: NamedNode) {
  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute("role");
  return (
    ["button", "a", "input", "select", "textarea", "summary"].includes(tag) ||
    role === "button" ||
    role === "tab" ||
    role === "checkbox" ||
    role === "menuitem" ||
    role === "switch"
  );
}

/** 조상을 거슬러 가장 가까운 구역 이름. 없으면 빈 문자열. */
export function activityArea(el: NamedNode): string {
  for (let n: NamedNode | null = el; n; n = n.parentElement) {
    const area = n.getAttribute("data-activity-area");
    if (area) return area;
    const tag = n.tagName.toLowerCase();
    const role = n.getAttribute("role");
    if (role === "dialog" || role === "alertdialog" || tag === "dialog") return "창";
    if (tag === "aside" || tag === "nav" || role === "navigation") return "메뉴";
    if (tag === "header") return "상단바";
  }
  return "";
}

/** 요소 종류로 풀어 쓴 이름 — 이름을 못 찾았을 때도 «무엇을» 눌렀는지 대강은 읽히게. */
function kindName(el: NamedNode): string {
  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute("role");
  if (tag === "button" || role === "button") return "이름 없는 버튼";
  if (tag === "input") return INPUT_KIND[el.getAttribute("type") || ""] || "입력칸";
  if (tag === "textarea") return "글 입력칸";
  if (tag === "select") return "고르기 상자";
  if (tag === "summary") return "접기·펼치기";
  if (tag === "form") return "양식 제출";
  if (tag === "label") return "항목 이름";
  if (role === "tab") return "탭";
  if (role === "checkbox" || role === "switch") return "체크 상자";
  if (role === "menuitem") return "메뉴 항목";
  if (TABLE.has(tag)) return "표 클릭";
  if (LIST.has(tag)) return "목록 클릭";
  if (HEADING.has(tag)) return "제목 클릭";
  if (PICTURE.has(tag)) return "그림 클릭";
  return "본문 클릭";
}

function actionOf(el: NamedNode, origin: string): string {
  const explicit = explicitOf(el);
  if (explicit) return explicit;
  const tag = el.tagName.toLowerCase();
  const isField = tag === "input" || tag === "textarea" || tag === "select";
  // aria-label·title 은 고정 문구일 때만. 버튼 글자도 고정 문구일 때만 — 입력칸은 글자(=값)를 아예 안 본다.
  const label =
    fixedLabel(el.getAttribute("aria-label")) ??
    fixedLabel(el.getAttribute("title")) ??
    (!isField && tag !== "a" && isInteractive(el) ? fixedLabel(el.textContent) : null);
  if (label) return isField ? `${label} · ${kindName(el)}` : label;
  // 링크 목적지는 경로만(동적 조각은 :id). 쿼리스트링엔 검색어가 실리므로 버린다.
  if (tag === "a") {
    const href = el.getAttribute("href");
    try {
      const url = new URL(href || "", origin || "http://localhost");
      if (href && (!origin || url.origin === origin)) return `이동 ${normalizeActivityPath(url.pathname)}`;
    } catch {
      /* invalid link */
    }
    return "외부 링크";
  }
  // 같은 구역 안에서 이름 붙은 조상(눌리는 줄·카드)을 찾는다 — 구역 경계를 넘으면 다른 칸의 이름이라 멈춘다.
  for (let n = el.parentElement; n; n = n.parentElement) {
    const named = explicitOf(n);
    if (named) return isInteractive(el) ? `${named} · ${kindName(el)}` : named;
    if (n.getAttribute("data-activity-area")) break;
  }
  return kindName(el);
}

/** 기록에 남길 이름 — `구역 · 행동`(구역이 없으면 행동만). 감춘 칸 안이면 «가린 칸». */
export function actionName(el: NamedNode, origin = typeof location === "undefined" ? "" : location.origin): string {
  if (isPrivate(el)) return "가린 칸";
  const action = actionOf(el, origin);
  const area = activityArea(el);
  // 마지막 그물 — data-* 이름표에 실수로 번호가 들어가도 남지 않게.
  return redactActivityText((area && !action.startsWith(area) ? `${area} · ${action}` : action).slice(0, 100));
}
